import styled from '@emotion/styled';
import {
  type AllReadsQuery,
  DBTimestampType,
  RacemapAPIClient,
} from '@racemap/utilities/api-client';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import type { Reader } from '@racemap/utilities/types/types';
import type { Immutable } from 'immer';
import { type FC, useState } from 'react';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import useSWR from 'swr';
import { useStore } from '../../store/reducers';
import { FetchReadsOptions } from './FetchReadsOptions';
import { ItemsOverOffsetChart } from './ItemsOverOffsetChart';
import { ReadersList } from './ReadersList/ReadersList';
import { ReadersMap } from './ReadersMap';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const ReadersSubTab: FC = () => {
  const event = useCurrentEvent();
  const readsFilter = useStore((s) => s.prediction.readsFilter);
  const [readerInFocus, setReaderInFocus] = useState<Reader | null>(null);
  const [hoveredPosition, setHoveredPosition] = useState<number | null>(null);
  const [positionToMoveTo, setPositionToMoveTo] = useState<{ lat: number; lng: number } | null>(
    null,
  );

  const readersFilter: AllReadsQuery = {
    eventId: readsFilter.eventId,
    startTime: readsFilter.startTime,
    endTime: readsFilter.endTime,
    timestampFilterType: DBTimestampType.ReadAt,
    userIds: readsFilter.userIds != null ? [...readsFilter.userIds] : undefined,
    isTest: false,
  };

  const predictionIsEnabled = event?.modules.predictive.enabled;
  const { data: readers } = useSWR<Array<Reader>>(
    predictionIsEnabled ? ['Readers', readersFilter] : null,
    async ([, readersFilter]: [string, AllReadsQuery]) => {
      return await apiClient.prediction().getAllReadersForEvent(readersFilter);
    },
    { refreshInterval: OneMinuteInMillis, keepPreviousData: false },
  );

  const timeFrames = {
    eventStart: event?.startTime ? new Date(event?.startTime).getTime() : null,
    eventEnd: event?.endTime ? new Date(event?.endTime).getTime() : null,
    searchWindowStart: readsFilter.startTime.getTime(),
    searchWindowEnd: readsFilter.endTime.getTime(),
    resultWindowStart: null,
    resultWindowEnd: null,
  };

  const handleClickOnReaderInFocus = (reader: Immutable<Reader>) => {
    if (reader.location?.lat != null && reader.location?.lng != null) {
      setPositionToMoveTo({ lat: reader.location.lat, lng: reader.location.lng });
    }
  };

  return (
    <Container>
      <FetchReadsOptions timeFrames={timeFrames} showFilterControls={false} />

      <TabContent>
        <ReadersMap
          readers={readers ?? []}
          hoveredPosition={hoveredPosition}
          readerInFocus={readerInFocus}
          positionToMoveTo={positionToMoveTo}
          predictionParams={event?.modules.predictive.params || null}
          onReaderInFocusChange={setReaderInFocus}
        />
        <ReadersList
          readers={readers ?? []}
          readerInFocus={readerInFocus}
          onReaderInFocusChange={setReaderInFocus}
          onReaderInFocusClick={handleClickOnReaderInFocus}
        />
        <ItemsOverOffsetChart
          readers={readers ?? []}
          withSplitChart
          onHoverPosition={setHoveredPosition}
          readerInFocus={readerInFocus}
          onReaderInFocusChange={setReaderInFocus}
        />
      </TabContent>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding-top: 30px;
`;

const TabContent = styled.div`
  display: grid;
  grid-template-columns: auto 300px;
  grid-template-areas: 'map list';
  height: 100%;
  position: relative;
`;
